/**
 * useZapper hook.
 */
import AnalyticsInstance from '@shared/utils/analytics';
import Channel from '@shared/classes/channel';
import ChannelUtils from '@shared/utils/channels';
import Focus from '@shared/types/enums/focus';
import Modals from '@shared/types/enums/modals';
import ParentalUtils from '@shared/utils/parental';
import Routes from '@shared/types/enums/routes';
import Storage, { PILOT_HINT_KEY } from '@shared/utils/storage';
import goToChannelList from '@shared/actions/channels/goToChannelList';
import useKeyboard from '@shared/components/utils/hooks/useKeyboard';
import usePauseAds from '@shared/components/utils/hooks/usePauseAds';
import { Coordinates } from '@shared/utils/floatingMenu';
import { PilotType } from '@shared/types/props/pilot';
import { PlayerState, TimeShift } from '@shared/types/enums/player';
import { PlayerTemplateProps } from '@shared/types/props/player';
import { channelsSelector } from '@shared/store/slices/channels.slice';
import { magicRemoteSelector } from '@shared/store/slices/magicRemote.slice';
import { popRoute } from '@shared/store/thunks/route.thunk';
import { clearFocus, setFocus } from '@shared/store/slices/focus.slice';
import { resetPlayer } from '@shared/store/slices/player.slice';
import { setParams, routerSelector } from '@shared/store/slices/router.slice';
import { store } from '@shared/store/store';
import { updateCurrentEvent } from '@shared/store/thunks/events.thunk';
import { useAppDispatch, useAppSelector } from '@shared/store/hooks';
import { userSelector } from '@shared/store/slices/user.slice';
import { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { DELAY, DURATION } from '@shared/types/constants/animations';
import {
  HINT_DISPLAY_TIME,
  MINIMUM_LIVE_OFFSET,
  PILOT_DISPLAY_TIME,
} from '@shared/types/constants/player';
import {
  channelDown,
  channelUp,
  updateChannelRail,
} from '@shared/store/thunks/channels.thunk';
import {
  clearModal,
  modalSelector,
  setModal,
  setModalParams,
} from '@shared/store/slices/modal.slice';
import {
  closeFloatingMenu,
  openFloatingMenu,
} from '@shared/store/thunks/floatingMenu.thunk';
import {
  mediaSelector,
  seek,
  setLive,
  toggle as togglePlayer,
  setPosition,
  pause,
  play,
  setStartover,
  reset as resetMedia,
} from '@shared/store/slices/media.slice';

/**
 * Handles the zapper actions such as channel switching, timeshift, keyboard interactions, floating menu, and pause ads.
 */
export default function useLive(): PlayerTemplateProps {
  // ------------------------------------------------------------
  // Variables
  // ------------------------------------------------------------

  // Dispatch
  const dispatch = useAppDispatch();

  // Selectors
  const { currentChannel, currentIndex } = useAppSelector(channelsSelector);
  const { enabled } = useAppSelector(magicRemoteSelector);
  const { error, ready, state, timeshift } = useAppSelector(mediaSelector);
  const { current: modal } = useAppSelector(modalSelector);
  const {
    params: { current: currentParams },
  } = useAppSelector(routerSelector);
  const { extraPlans, parental, planId, userId } = useAppSelector(userSelector);

  // States
  const [allowPlayback, setAllowPlayback] = useState(true);
  const [hintVisible, setHintVisible] = useState(false);
  const [pilotVisible, setPilotVisible] = useState(false);

  // Refs
  const channelRef = useRef<Channel | null>();
  const enabledRef = useRef<boolean>();
  const errorRef = useRef(error);
  const firstPlayRef = useRef<boolean>(false);
  const hintTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const hintVisibleRef = useRef<boolean>(false);
  const modalRef = useRef<Modals | null>();
  const pilotVisibleRef = useRef<boolean>();
  const pilotTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const readyRef = useRef<boolean>();
  const timeshiftRef = useRef<boolean>();
  const userInteraction = useRef<boolean>(false);
  channelRef.current = currentChannel;
  enabledRef.current = enabled;
  errorRef.current = error;
  hintVisibleRef.current = hintVisible;
  pilotVisibleRef.current = pilotVisible;
  readyRef.current = ready;
  timeshiftRef.current = timeshift;
  userInteraction.current =
    state === PlayerState.SEEKING || state === PlayerState.PAUSE; // Track user interaction when seeking or pausing

  // ------------------------------------------------------------
  // Effect hooks
  // ------------------------------------------------------------

  /**
   * Initialize zapper: set focus, mark as live, and update current event
   */
  useEffect(() => {
    dispatch(setFocus(Focus.ZAPPER));
    dispatch(setLive());
    dispatch(resetMedia());
    dispatch(updateCurrentEvent());
    return () => {
      clearTimeout(hintTimeoutRef.current);
      clearTimeout(pilotTimeoutRef.current);
      dispatch(closeFloatingMenu());
      dispatch(clearFocus());
      dispatch(clearModal());
      dispatch(resetPlayer());
      dispatch(resetMedia());
    };
  }, []);

  /**
   * Reset user interaction flag and modal state when channel changes
   */
  useEffect(() => {
    userInteraction.current = false;
    modalRef.current = null;
  }, [currentIndex]);

  /**
   * Reset playback when channel changes & create analytics playback session when channel changes.
   * Also hides the pilot hint when changing channel (pilot may stay visible).
   */
  useEffect(() => {
    // Reset playback
    setAllowPlayback(false);
    dispatch(updateChannelRail());
    // Hide pilot hint when changing channel (pilot may stay visible)
    clearTimeout(hintTimeoutRef.current);
    setHintVisible(false);
    // Create analytics playback session
    AnalyticsInstance.createPlaybackSession(
      currentChannel,
      currentChannel?.currentEvent || undefined
    );
  }, [currentChannel]);

  /**
   * Update analytics event when current event changes
   */
  useEffect(() => {
    if (AnalyticsInstance.getOmnitrackManager()?.getPlaybackSession()) {
      const isDifferentEvent =
        AnalyticsInstance.getOmnitrackManager()?.getPlaybackSession()
          ?.event_id !== currentChannel?.currentEvent?.eventId;
      if (currentChannel?.currentEvent && isDifferentEvent) {
        AnalyticsInstance.setNewEvent(currentChannel?.currentEvent);
      }
    }
  }, [currentChannel?.currentEvent]);

  /**
   * Handle modal display logic based on channel access, parental controls, and errors
   */
  useEffect(() => {
    let newModal = null;
    const plan = ChannelUtils.getUpgradablePlan(currentChannel);
    const { user } = store.getState();

    // Dispatch anonymous modal / not allowed channel
    if (plan) {
      const launchAnonymousModal = () => {
        const { modal } = store.getState();
        if (modal.current !== Modals.ACTIVATE_GENERIC) {
          store.dispatch(setModal(Modals.ANONYMOUS_ZAPPER));
        }
      };
      !user.isAnonymous
        ? store.dispatch(setModal(Modals.CHANNEL_NOT_ALLOWED))
        : launchAnonymousModal();
    }
    // Dispatch parental pin modal
    else if (
      modal === null &&
      parental?.pin &&
      parental?.enabled &&
      ParentalUtils.isTimeRestricted(parental?.timeFrames) &&
      ParentalUtils.isAgeRestricted(currentChannel?.currentEvent?.ageCode)
    ) {
      newModal = Modals.PARENTAL_PIN_ZAPPER;
      store.dispatch(setModal(Modals.PARENTAL_PIN_ZAPPER));
    }
    // Check player error
    else if (modal === Modals.PLAYER_ERROR) {
      togglePilot(false);
    }
    // Check any modal and set playback
    else {
      const allowPlayback =
        modal !== null
          ? ![
              Modals.ACTIVATE_ZAPPER,
              Modals.ANONYMOUS_ZAPPER,
              Modals.CHANNEL_NOT_ALLOWED,
              Modals.PARENTAL_PIN_ZAPPER,
              Modals.PLAYER_ERROR,
            ].includes(modal)
          : true;
      setAllowPlayback(allowPlayback);
      allowPlayback && togglePilot(modal === null);
    }

    // Set current modal
    modalRef.current = newModal || modal;
  }, [currentChannel, extraPlans, modal, parental, planId, userId]);

  /**
   * Handle restart and timeshift position restoration on first play
   */
  useEffect(() => {
    if (state === PlayerState.PLAYING && currentChannel?.currentEvent) {
      // Handle restart from beginning
      if (currentParams?.restart) {
        // Restart
        if (currentChannel?.currentEvent?.date?.start) {
          if (currentParams?.position === 0) {
            dispatch(setStartover(true));
            dispatch(
              setParams({
                ...currentParams,
                position: undefined,
                restart: undefined,
              })
            );
            dispatch(
              setPosition({
                position: 0,
                seek: true,
              })
            );
          }
        }
      }
      // Handle timeshift
      else if (
        currentParams?.timeshift &&
        !firstPlayRef.current &&
        currentParams?.channelId === currentChannel?.id
      ) {
        dispatch(
          setPosition({
            position: currentParams.position as number,
            seek: true,
          })
        );
      }
      firstPlayRef.current = true;
    }
  }, [currentChannel, currentParams, state]);

  // ------------------------------------------------------------
  // Actions
  // ------------------------------------------------------------

  /**
   * Selects the hint button and confirms the message won't be shown again.
   */
  const selectHint = useCallback(() => {
    // Get the user ID, if empty, return
    const { user } = store.getState();
    const userId = user.isAnonymous ? 'anonymous' : user.userId;
    if (!userId) return;

    // Parse the stored value
    const storedValue = Storage.get(PILOT_HINT_KEY);
    const parsedValue = JSON.parse(storedValue ?? '{}');

    // Check if the user ID is in the parsed value and if not, add it
    if (!parsedValue.hasOwnProperty(userId)) {
      parsedValue[userId] = true;
      Storage.set(PILOT_HINT_KEY, JSON.stringify(parsedValue));
    }

    // Hide hint
    toggleHint(false);
  }, []);

  /**
   * Toggles the floating menu visibility.
   * If opening, injects timeshift position into route params if not on live position.
   */
  const toggleFloatingMenu = useCallback((open = true) => {
    // If the channel has no event, do nothing
    if (!channelRef.current?.currentEvent?.id) return;
    // Open the floating menu
    if (open) {
      const route = {
        asset: channelRef.current?.currentEvent || null,
        position: Coordinates.ZAPPER,
        route: Routes.ZAPPER,
      };
      const { media } = store.getState();
      const offset = (Date.now() - media.position) / 1000;
      // Inject position if not on live position (timeshifting)
      if (media.timeshift && offset > MINIMUM_LIVE_OFFSET) {
        dispatch(
          setParams({
            channelId: channelRef.current?.id,
            position: media.position,
            timeshift: true,
          })
        );
      }
      dispatch(openFloatingMenu(route));
    } else {
      dispatch(closeFloatingMenu());
    }
  }, []);

  /**
   * Toggles the hint (dial hint) visibility.
   * Auto-hides after HINT_DISPLAY_TIME.
   */
  const toggleHint = useCallback(
    (display = true) => {
      clearTimeout(hintTimeoutRef.current);
      if (display) {
        setHintVisible(true);
        hintTimeoutRef.current = setTimeout(() => {
          toggleHint(false);
        }, HINT_DISPLAY_TIME);
      } else {
        clearTimeout(hintTimeoutRef.current);
        setHintVisible(false);
        setPilotVisible(false);
      }
    },
    [dispatch]
  );

  /**
   * Toggles the pilot (channel info overlay) visibility.
   * When hiding from the pilot auto-timeout only, if the pilot hint key is not yet set
   * in storage, shows the dial hint first (pilot stays visible until hint dismisses).
   * Any other hide (Back, errors, etc.) clears the hint first and hides the pilot.
   * Auto-hides after PILOT_DISPLAY_TIME unless floating menu is open or seeking.
   */
  const togglePilot = useCallback(
    (display = true, options?: { fromPilotTimeout?: boolean }) => {
      // Clear modal if displaying pilot (except parental pin modal)
      if (
        modalRef.current &&
        modalRef.current !== Modals.PARENTAL_PIN_ZAPPER &&
        !errorRef.current.code
      ) {
        dispatch(clearModal());
      }
      clearTimeout(pilotTimeoutRef.current);
      if (display) {
        // Auto-hide pilot after timeout, unless floating menu is open or seeking
        pilotTimeoutRef.current = setTimeout(() => {
          const {
            focus: { focus },
            media: { state },
          } = store.getState();
          const shouldDisplay =
            focus === Focus.FLOATING_MENU ||
            state === PlayerState.SEEKING ||
            state === PlayerState.PAUSE;
          togglePilot(shouldDisplay, { fromPilotTimeout: true });
        }, PILOT_DISPLAY_TIME);
      } else {
        // Hide hint if not from pilot timeout
        const fromPilotTimeout = options?.fromPilotTimeout === true;
        if (!fromPilotTimeout) {
          toggleHint(false);
          return;
        }
        // Check if the user has acknowledged the hint
        const { user } = store.getState();
        const storedValue = Storage.get(PILOT_HINT_KEY);
        const parsedValue = JSON.parse(storedValue ?? '{}');
        const userId = user.isAnonymous ? 'anonymous' : user.userId;
        if (!parsedValue.hasOwnProperty(userId)) {
          toggleHint(true);
          return;
        }
      }
      // Show or hide the pilot
      setPilotVisible(display);
    },
    [dispatch, toggleHint]
  );

  // ------------------------------------------------------------
  // Event handlers
  // ------------------------------------------------------------

  /**
   * Handles back event.
   * Shows the close player modal if the pilot is not visible and the magic remote is enabled.
   * Hides the pilot.
   */
  const onBack = useCallback(() => {
    if (!pilotVisibleRef.current || enabledRef.current) {
      // Check if the user is behind live
      const { media } = store.getState();
      const isBehindLive =
        (Date.now() - media.position) / 1000 > MINIMUM_LIVE_OFFSET;
      // If behind live, show close player modal; otherwise navigate back
      isBehindLive
        ? dispatch(setModal(Modals.CLOSE_PLAYER))
        : dispatch(popRoute());
    }
    togglePilot(false);
  }, []);

  /**
   * Handles magic remote movement to show pilot overlay.
   * Only shows if no modal is currently displayed.
   */
  const onMouseMove = useCallback(() => {
    if (modalRef.current) return;
    hintVisibleRef.current ? toggleHint() : togglePilot();
  }, []);

  /**
   * Handles select events.
   * Shows pilot if requested, otherwise handles select or close player modal.
   */
  const onSelect = useCallback(() => {
    // If the hint is visible, confirm the message won't be shown again and hide the hint
    if (hintVisibleRef.current) {
      selectHint();
      return;
    }
    // If the pilot is not visible, show it
    if (!pilotVisibleRef.current) {
      togglePilot();
      return;
    }
    // Else, toggle the player
    dispatch(togglePlayer());
  }, []);

  /**
   * Handles horizontal navigation events.
   * Handles seek backward and forward. Shows channel list if offset is less than MINIMUM_LIVE_OFFSET.
   */
  const onHorizontalNavigation = useCallback((key: string) => {
    // If the channel has no event, do nothing
    if (!channelRef.current?.currentEvent?.id) return;
    // If the hint is visible, do nothing
    if (hintVisibleRef.current) return;
    // Timeshift backwards if the key is left or rewind
    if (key === 'LEFT' || key === 'REWIND') {
      // Seek backward to timeshift
      dispatch(seek(TimeShift.BACKWARDS));
    }
    // Timeshift forward / go to channel list if the key is right or fastforward
    else if (key === 'RIGHT' || key === 'FASTFORWARD') {
      // Seek forward to timeshift
      const { ads, media } = store.getState();
      const offset = (Date.now() - media.position) / 1000;
      const isFastForward = key === 'FASTFORWARD';
      const isLive = offset < MINIMUM_LIVE_OFFSET;
      const isPlayingOrPaused =
        media.state === PlayerState.PLAYING ||
        media.state === PlayerState.PAUSE;
      // If the player is playing or paused and the offset is less than MINIMUM_LIVE_OFFSET, go to channel list
      if (!isFastForward && isLive && isPlayingOrPaused) {
        if (ads.pauseAd || pilotVisibleRef.current) {
          goToChannelList();
        } else {
          togglePilot();
        }
      } else {
        dispatch(seek(TimeShift.FORWARD));
      }
    }
  }, []);

  /**
   * Handles channel change events.
   * Shows pilot if requested, otherwise handles channel switching or close player modal.
   */
  const onVerticalNavigation = useCallback((key: string) => {
    // Check if the pilot should be shown
    if (key === 'UP' || key === 'DOWN') {
      const shouldShowPilot =
        !pilotVisibleRef.current &&
        modalRef.current !== Modals.CHANNEL_NOT_ALLOWED;
      if (shouldShowPilot) {
        togglePilot();
        return;
      }
    }

    // Change channel logic
    const { media } = store.getState();
    const offset = (Date.now() - media.position) / 1000;
    const isBehindLive = offset > MINIMUM_LIVE_OFFSET;

    // If timeshifting, show close player modal; otherwise switch channels
    if (
      isBehindLive &&
      media.ready &&
      media.timeshift &&
      userInteraction.current
    ) {
      dispatch(setModal(Modals.CLOSE_PLAYER));
      dispatch(setModalParams({ key }));
    } else {
      if (key === 'CHANNEL_UP' || key === 'UP') {
        dispatch(channelUp());
      } else if (key === 'CHANNEL_DOWN' || key === 'DOWN') {
        dispatch(channelDown());
      }
    }
  }, []);

  // ------------------------------------------------------------
  // Keyboard handlers
  // ------------------------------------------------------------

  /**
   * Handles key down events for player controls and navigation.
   * Manages timeshift seeking, channel switching, playback control, and pilot display.
   */
  const keyDownHandler = (key: string) => {
    // Handle key down events
    switch (key) {
      case 'REWIND':
      case 'LEFT':
      case 'FASTFORWARD':
      case 'RIGHT':
        onHorizontalNavigation(key);
        break;
      case 'UP':
      case 'DOWN':
      case 'CHANNEL_UP':
      case 'CHANNEL_DOWN':
        onVerticalNavigation(key);
        break;
      case 'PAUSE':
        dispatch(pause());
        break;
      case 'PLAY':
        dispatch(play());
        break;
      case 'PLAY_PAUSE':
        dispatch(togglePlayer());
        break;
    }
  };

  /**
   * Handles key press events for player controls and navigation.
   * Opens the floating menu.
   */
  const keyPressHandler = () => {
    toggleFloatingMenu();
  };

  /**
   * Handles key up events for player controls and navigation.
   * Handles back button/key press.
   */
  const keyUpHandler = (key: string) => {
    switch (key) {
      // Close player or display close player modal
      case 'BACK':
        onBack();
        break;
      case 'SELECT':
        onSelect();
        break;
      // Else, If the pilot is not visible, show it
      default:
        togglePilot();
        break;
    }
  };

  // ------------------------------------------------------------
  // Custom hooks
  // ------------------------------------------------------------

  /**
   * Keyboard hook. Handles key down, press, and up events for player controls and navigation.
   */
  useKeyboard({
    focus: Focus.ZAPPER,
    keyDownHandler,
    keyPressHandler,
    keyUpHandler,
  });

  /**
   * Pause ads hook. Displays pause ads if enabled.
   */
  usePauseAds({
    asset: currentChannel?.currentEvent ?? undefined,
    keyHandler: keyDownHandler,
    useKeyboard: useKeyboard,
  });

  // ------------------------------------------------------------
  // Memoized values
  // ------------------------------------------------------------

  return useMemo(() => {
    return {
      display: {
        player: !!allowPlayback,
        spinner: false,
        ui: !!(currentChannel && allowPlayback),
      },
      callbacks: {
        container: {
          onMouseMove,
        },
      },
      props: {
        container: {
          delay: DELAY.PLAYER_CONTAINER,
          duration: DURATION.PLAYER_CONTAINER,
        },
        pilot: {
          channel: currentChannel,
          display: pilotVisible,
          hint: {
            action: selectHint,
            visible: hintVisible,
          },
          type: PilotType.LIVE,
        },
        player: {
          channel: currentChannel,
        },
      },
    };
  }, [currentChannel, allowPlayback, hintVisible, pilotVisible, onMouseMove]);
}
